import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HandHeart, Loader2, TriangleAlert } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import {
  listDonations,
  listRecurringDonations,
  postAwaitingDonation,
  formatOre,
  type BoardDonation,
  type BoardRecurringDonation,
  type DonationStatus,
  type RecurringStatus,
} from '../lib/donations';

type Load = 'loading' | 'ready' | 'error';
type Notice = { tone: 'success' | 'error'; text: string } | null;

/** Status → visual treatment. Muted by default; awaiting draws the eye (it needs board action). */
const STATUS_STYLE: Record<DonationStatus, string> = {
  paid: 'border-accent/40 bg-accent/10 text-ink',
  awaiting_posting: 'border-error/40 bg-error/5 text-error',
  pending: 'border-border bg-surface text-muted',
  failed: 'border-border bg-surface text-muted',
  cancelled: 'border-border bg-surface text-muted',
  refunded: 'border-border bg-surface text-muted line-through',
};
const STATUS_LABEL: Record<DonationStatus, string> = {
  paid: 'Paid',
  awaiting_posting: 'Awaiting posting',
  pending: 'Pending',
  failed: 'Failed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

/** Recurring status → treatment. Active is highlighted; past_due draws the eye (dunning). */
const RECURRING_STYLE: Record<RecurringStatus, string> = {
  active: 'border-accent/40 bg-accent/10 text-ink',
  past_due: 'border-error/40 bg-error/5 text-error',
  pending: 'border-border bg-surface text-muted',
  cancelled: 'border-border bg-surface text-muted',
  incomplete_expired: 'border-border bg-surface text-muted',
};
const RECURRING_LABEL: Record<RecurringStatus, string> = {
  active: 'Active',
  past_due: 'Past due',
  pending: 'Pending',
  cancelled: 'Cancelled',
  incomplete_expired: 'Expired',
};

/**
 * Spec 007 — board oversight of donations. Board/admin only (RequireRole; the server RolesGuard is the
 * real authority). Lists every donation with its status, amount, donor-or-"Anonymous", and a link to the
 * ledger entry it posted. An `awaiting_posting` row (no open year at confirm time) gets a "Post" action to
 * book it into the open books. Four states: loading → list → empty ("no donations yet") → error.
 */
function DonationsInner() {
  const [load, setLoad] = useState<Load>('loading');
  const [rows, setRows] = useState<BoardDonation[]>([]);
  const [recurring, setRecurring] = useState<BoardRecurringDonation[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [postingId, setPostingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [oneOff, rec] = await Promise.all([listDonations(), listRecurringDonations()]);
    if (oneOff.kind === 'ok' && rec.kind === 'ok') {
      setRows(oneOff.data);
      setRecurring(rec.data);
      setLoad('ready');
    } else {
      setLoad('error');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onPost(id: string) {
    setNotice(null);
    setPostingId(id);
    const r = await postAwaitingDonation(id);
    setPostingId(null);
    if (r.kind === 'ok') {
      setNotice({ tone: 'success', text: 'Donation booked into the open books.' });
      void refresh();
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
      void refresh();
    } else if (r.kind === 'forbidden') {
      setNotice({ tone: 'error', text: r.message });
    } else {
      setNotice({ tone: 'error', text: 'Couldn’t post that donation. Please try again.' });
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-16">
      <header className="flex items-center gap-3">
        <HandHeart aria-hidden className="accent-glow h-7 w-7 text-accent" />
        <div>
          <h1 className="font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Donations
          </h1>
          <p className="font-serif text-base text-muted">
            Every gift that reaches the order. Confirmed donations post to the open books automatically.
          </p>
        </div>
      </header>

      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`mt-6 rounded-md border px-3 py-2 font-sans text-sm ${
            notice.tone === 'error'
              ? 'border-error/40 bg-error/5 text-error'
              : 'border-accent/40 bg-accent/10 text-ink'
          }`}
        >
          {notice.text}
        </p>
      )}

      {load === 'loading' && (
        <div className="mt-16 flex flex-col items-center" aria-live="polite">
          <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
          <p className="mt-3 font-serif text-lg text-muted">Loading donations…</p>
        </div>
      )}

      {load === 'error' && (
        <p
          role="alert"
          className="mt-8 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error"
        >
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load the donations. Please refresh and try again.
        </p>
      )}

      {load === 'ready' && rows.length === 0 && (
        <div className="mt-16 rounded-md border border-dashed border-border bg-surface px-6 py-12 text-center">
          <p className="font-serif text-lg text-muted">No donations recorded yet.</p>
          <p className="mt-1 font-sans text-sm text-muted">
            When someone gives through the public donation page, it will appear here.
          </p>
        </div>
      )}

      {load === 'ready' && rows.length > 0 && (
        <ul className="mt-8 divide-y divide-border overflow-hidden rounded-md border border-border">
          {rows.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-surface px-4 py-3">
              <span className="font-sans text-base font-medium tabular-nums text-ink">
                {formatOre(d.amountOre)}
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 font-sans text-xs ${STATUS_STYLE[d.status]}`}
              >
                {STATUS_LABEL[d.status]}
              </span>
              <span className="font-serif text-sm text-muted">{d.donor ?? 'Anonymous'}</span>
              <span className="ml-auto flex items-center gap-3">
                {d.ledgerEntryId && (
                  <Link
                    to="/finances"
                    className="font-sans text-xs text-accent underline-offset-2 hover:underline"
                  >
                    In the books
                  </Link>
                )}
                {d.status === 'awaiting_posting' && (
                  <button
                    type="button"
                    onClick={() => onPost(d.id)}
                    disabled={postingId === d.id}
                    className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1 font-sans text-xs font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
                  >
                    {postingId === d.id ? (
                      <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    Post to books
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {load === 'ready' && (
        <section className="mt-12" aria-labelledby="recurring-heading">
          <h2
            id="recurring-heading"
            className="font-display text-lg font-semibold uppercase tracking-[0.12em] text-ink"
          >
            Recurring donations
          </h2>
          <p className="mt-1 font-serif text-sm text-muted">
            Monthly gifts. Each paid renewal posts to the open books like any other donation.
          </p>

          {recurring.length === 0 ? (
            <div className="mt-6 rounded-md border border-dashed border-border bg-surface px-6 py-10 text-center">
              <p className="font-serif text-base text-muted">No recurring donations.</p>
            </div>
          ) : (
            <ul className="mt-6 divide-y divide-border overflow-hidden rounded-md border border-border">
              {recurring.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-surface px-4 py-3">
                  <span className="font-sans text-base font-medium tabular-nums text-ink">
                    {formatOre(r.amountOre)}
                    <span className="font-normal text-muted"> / month</span>
                  </span>
                  <span
                    className={`rounded-full border px-2 py-0.5 font-sans text-xs ${RECURRING_STYLE[r.status]}`}
                  >
                    {RECURRING_LABEL[r.status]}
                  </span>
                  <span className="font-serif text-sm text-muted">{r.donor ?? 'Anonymous'}</span>
                  <span className="ml-auto font-sans text-xs text-muted">
                    {r.renewalCount} {r.renewalCount === 1 ? 'renewal' : 'renewals'} posted
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

export default function Donations() {
  return <RequireRole min="board">{() => <DonationsInner />}</RequireRole>;
}
